import { useState } from "react";

import { CopyButton } from "@/components/CopyButton";
import { Checkbox } from "@/components/form";
import { RelativeTime } from "@/components/RelativeTime";
import { Notice } from "@/components/ui";

// When the target AWS account already hosts another PulseStation environment,
// its account-level shared setup already exists and must be reused rather than
// recreated — recreating it makes the bootstrap fail. Flipping this query
// param tells the bootstrap stack to reuse the existing account setup.
const REUSE_ACCOUNT_PARAM = "&param_CreateGitHubOidcProvider=false";

export function LaunchStackPanel({
  url,
  expiresAt,
}: {
  url: string;
  expiresAt: number;
}) {
  const [accountAlreadyUsed, setAccountAlreadyUsed] = useState(false);
  const effectiveUrl = accountAlreadyUsed ? `${url}${REUSE_ACCOUNT_PARAM}` : url;

  return (
    // The expiry sits in the TITLE row, not in `actions`: an actions column takes its
    // width from the whole box, so the link below would shrink with the expiry text.
    // No `role`: this panel holds a checkbox and a link that changes with it, and a live
    // region would announce the whole panel again at every toggle.
    <Notice
      tone="warning"
      title={
        <span className="flex flex-wrap items-baseline justify-between gap-x-3">
          <span>Installation link ready</span>
          <span className="text-xs font-normal">
            {/* Vraie échéance : un lien d'installation périmé ne marche plus,
                le rouge est ici le bon signal. */}
            Expires <RelativeTime epochMs={expiresAt} deadline />
          </span>
        </span>
      }
    >
      <p className="mt-1 text-xs">
        Send this one-time link to the tenant's cloud operator.
        Opening it walks through the guided setup, with all parameters pre-filled.
      </p>
      <Checkbox
        className="mt-3"
        label="This cloud account already hosts another PulseStation environment."
        description="Check this if the installation is happening in an already configured account (a second tenant or environment) — the account's shared setup is reused instead of being recreated."
        checked={accountAlreadyUsed}
        onChange={(e) => setAccountAlreadyUsed(e.target.checked)}
      />
      <div className="mt-3 flex items-center gap-2">
        <a
          href={effectiveUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 truncate rounded border border-line bg-surface px-3 py-2 font-mono text-xs text-brand-700 hover:underline"
        >
          {effectiveUrl}
        </a>
        <CopyButton value={effectiveUrl} />
      </div>
    </Notice>
  );
}
